import { FC, useState } from 'react';
import {
  Button,
  Upload,
  Table,
  Progress,
  Card,
  Space,
  Typography,
  Alert,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import SidebarPage from '../../../layout/SidebarPage/SidebarPage';
import PageTitle from '../../../layout/PageTitle/PageTitle';
import { usePost } from '../../../hooks/useApi';
import {
  ElopImportResponse,
  ElopImportRow,
} from '../../../models/admin/elop.model';

const { Text } = Typography;

const ElopImport: FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<ElopImportRow[]>([]);
  const [summary, setSummary] = useState<{
    processed: number;
    successful: number;
    failed: number;
  } | null>(null);

  const uploadMutation = usePost<ElopImportResponse, FormData>(
    '/v1/auth/admin/elop/import',
    {
      onSuccess: (data) => {
        setUploading(false);
        setResults(data.results);
        setSummary({
          processed: data.processed,
          successful: data.successful,
          failed: data.failed,
        });
        message.success(
          `Import completed: ${data.successful} successful, ${data.failed} failed`
        );
        setFileList([]);
      },
      onError: (error: any) => {
        setUploading(false);
        message.error(error.message || 'Import failed');
      },
    }
  );

  const handleDownloadTemplate = () => {
    // Create CSV template content
    const templateContent =
      'Email,Name,Grade,DistrictOrgAccountId\n' +
      'parent@example.com,"Doe, John",K,\n' +
      'another@example.com,"Smith, Jane",1,123\n';

    // Create blob and download
    const blob = new Blob([templateContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'elop-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.warning('Please select a CSV file to upload');
      return;
    }

    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('file', file as RcFile);
    });

    setUploading(true);
    setResults([]);
    setSummary(null);
    uploadMutation.mutate(formData);
  };

  const beforeUpload = (file: RcFile) => {
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
    if (!isCSV) {
      message.error('You can only upload CSV files!');
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('CSV file must be smaller than 10MB!');
      return false;
    }

    setFileList([file]);
    return false; // Prevent auto upload
  };

  const columns: ColumnsType<ElopImportRow> = [
    {
      title: 'Row',
      dataIndex: 'row',
      key: 'row',
      width: 80,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Grade',
      dataIndex: 'grade',
      key: 'grade',
      width: 100,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) =>
        record.success ? (
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <Text type="success">Success</Text>
          </Space>
        ) : (
          <Space>
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            <Text type="danger">Failed</Text>
          </Space>
        ),
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      render: (error: string) =>
        error ? <Text type="danger">{error}</Text> : '-',
    },
  ];

  return (
    <SidebarPage>
      <PageTitle>ELO-P Import</PageTitle>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card title="Upload CSV File">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text>
                Download the CSV template, fill it with ELO-P account data, and
                upload it here.
              </Text>
            </div>

            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              type="default"
            >
              Download CSV Template
            </Button>

            <Upload
              fileList={fileList}
              beforeUpload={beforeUpload}
              onRemove={() => setFileList([])}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select CSV File</Button>
            </Upload>

            <Button
              type="primary"
              onClick={handleUpload}
              disabled={fileList.length === 0}
              loading={uploading}
              icon={<UploadOutlined />}
            >
              {uploading ? 'Uploading...' : 'Upload and Process'}
            </Button>

            {uploading && (
              <Progress
                percent={50}
                status="active"
                showInfo={false}
                strokeColor={{ from: '#108ee9', to: '#87d068' }}
              />
            )}
          </Space>
        </Card>

        {summary && (
          <Card title="Import Summary">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Text strong>Total Processed: </Text>
                <Text>{summary.processed}</Text>
              </div>
              <div>
                <Text strong>Successful: </Text>
                <Text type="success">{summary.successful}</Text>
              </div>
              <div>
                <Text strong>Failed: </Text>
                <Text type="danger">{summary.failed}</Text>
              </div>
            </Space>
          </Card>
        )}

        {results.length > 0 && (
          <Card title="Import Results">
            <Table
              columns={columns}
              dataSource={results}
              rowKey={(record) => `row-${record.row}`}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              size="small"
            />
          </Card>
        )}

        {results.length === 0 && !uploading && (
          <Alert
            message="No results yet"
            description="Upload a CSV file to see import results"
            type="info"
            showIcon
          />
        )}
      </Space>
    </SidebarPage>
  );
};

export default ElopImport;
